import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { Shield, UserPlus, Trash2, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const { user, authHeaders, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [discordId, setDiscordId] = useState('');
  const [robloxUsername, setRobloxUsername] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!loading && (!user || !user.isAdmin)) navigate('/');
  }, [user, loading, navigate]);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/overrides`, { headers: authHeaders });
      setItems(r.data || []);
    } catch {}
  };

  useEffect(() => { if (user?.isAdmin) load(); /* eslint-disable-next-line */ }, [user?.isAdmin]);

  const add = async (e) => {
    e.preventDefault();
    if (!discordId.trim()) return;
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/admin/overrides`, {
        discord_id: discordId.trim(),
        roblox_username: robloxUsername.trim() || null,
        note: note.trim() || null,
      }, { headers: authHeaders });
      setDiscordId(''); setRobloxUsername(''); setNote('');
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Ekleme başarısız');
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Kullanıcıyı listeden kaldırmak istiyor musunuz?')) return;
    await axios.delete(`${API}/admin/overrides/${id}`, { headers: authHeaders });
    await load();
  };

  if (loading || !user?.isAdmin) return null;

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <Header />
      <main className="max-w-5xl mx-auto px-5 lg:px-8 pt-10 pb-24">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ffd27d, #ff6fa8)' }}>
            <Sparkles size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight flex items-center gap-2">
              Admin Panel
              <span className="text-[10px] px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 font-semibold">RESTRICTED</span>
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Discord ID'siyle manuel kullanıcı yetkisi ver.</p>
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          <form onSubmit={add} className="lg:col-span-1 card-soft rounded-2xl p-6 flex flex-col gap-3">
            <h2 className="text-lg font-semibold flex items-center gap-2"><UserPlus size={16} /> Yeni Yetkili Ekle</h2>
            <label className="flex flex-col gap-1.5">
              <span className="editor-label">Discord User ID</span>
              <input required value={discordId} onChange={e => setDiscordId(e.target.value)} placeholder="123456789012345678" className="to-input" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="editor-label">Roblox Kullanıcı Adı (opsiyonel)</span>
              <input value={robloxUsername} onChange={e => setRobloxUsername(e.target.value)} placeholder="Roblox adı" className="to-input" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="editor-label">Not (opsiyonel)</span>
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="Neden ekleniyor?" className="to-input" />
            </label>
            {err && <div className="text-sm text-rose-500">{err}</div>}
            <button disabled={busy} className="pill-btn justify-center font-semibold" style={{ background: 'var(--text)', color: 'var(--bg)', borderColor: 'transparent' }}>
              {busy ? <Loader2 className="animate-spin" size={14} /> : <UserPlus size={14} />}
              <span>Ekle</span>
            </button>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              <Shield size={11} className="inline mr-1" /> Bu kişiye wiki düzenleme yetkisi verilir (rank'tan bağımsız).
            </p>
          </form>

          <div className="lg:col-span-2 card-soft rounded-2xl p-6">
            <h2 className="text-lg font-semibold">Manuel Eklenen Yetkililer ({items.length})</h2>
            <div className="mt-4 flex flex-col divide-y" style={{ borderColor: 'var(--border)' }}>
              {items.map(u => (
                <div key={u.discord_id} className="py-3 flex items-center gap-3">
                  {u.avatar ? <img src={u.avatar} alt="" className="w-10 h-10 rounded-full" /> : <div className="w-10 h-10 rounded-full" style={{ background: 'var(--pill-hover)' }} />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{u.discord_displayName || u.discord_username || u.discord_id}</div>
                    <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                      ID: {u.discord_id}
                      {u.roblox_username && <> · @{u.roblox_username}</>}
                      {u.note && <> · {u.note}</>}
                    </div>
                  </div>
                  <button onClick={() => remove(u.discord_id)} className="pill-btn !py-1.5 !px-3 text-xs" style={{ color: '#f43f5e' }}>
                    <Trash2 size={12} /> Kaldır
                  </button>
                </div>
              ))}
              {items.length === 0 && (
                <p className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Henüz manuel eklenen yetkili yok.</p>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
